import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import {
  TrendingUp,
  TrendingDown,
  Package,
  AlertTriangle,
  DollarSign,
  ShoppingCart,
  Users,
  BarChart3,
  Store,
  Activity
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from 'recharts';
import {
  getDashboardStats,
  getPriceTrends,
  getSupplierComparison,
  getShoppingSuggestions,
  getMovementStats
} from '../api';
import { useStructure } from '../contexts/StructureContext';

const Dashboard = () => {
  const { currentStructure } = useStructure();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [priceTrends, setPriceTrends] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [movementStats, setMovementStats] = useState(null);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('overview');

  useEffect(() => {
    if (currentStructure) {
      fetchDashboardData();
    }
  }, [currentStructure]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsResponse, trendsResponse, suppliersResponse, suggestionsResponse, movementStatsResponse] = await Promise.all([
        getDashboardStats(currentStructure.id),
        getPriceTrends(currentStructure.id, 90),
        getSupplierComparison(currentStructure.id),
        getShoppingSuggestions(currentStructure.id),
        getMovementStats(currentStructure.id)
      ]);

      setStats(statsResponse.data);
      setPriceTrends(trendsResponse.data.trends || []);
      setSuppliers(suppliersResponse.data.suppliers || []);
      setSuggestions(suggestionsResponse.data);
      setMovementStats(movementStatsResponse.data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Erreur lors du chargement du tableau de bord');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center py-12">Chargement du tableau de bord...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-red-100 text-red-800 p-4 rounded-lg">{error}</div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mb-1">{title}</p>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full bg-${color.split('-')[1]}-100`}>
            <Icon className={color} size={24} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Tableau de bord</h1>
        <p className="text-gray-600">Vue d'ensemble de votre gestion de stock</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b">
        <button
          onClick={() => setSelectedTab('overview')}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 transition ${
            selectedTab === 'overview'
              ? 'border-primary text-primary font-semibold'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <BarChart3 size={18} />
          Vue d'ensemble
        </button>
        <button
          onClick={() => setSelectedTab('trends')}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 transition ${
            selectedTab === 'trends'
              ? 'border-primary text-primary font-semibold'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <TrendingUp size={18} />
          Évolution des prix
        </button>
        <button
          onClick={() => setSelectedTab('suppliers')}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 transition ${
            selectedTab === 'suppliers'
              ? 'border-primary text-primary font-semibold'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <Store size={18} />
          Fournisseurs
        </button>
        <button
          onClick={() => setSelectedTab('shopping')}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 transition ${
            selectedTab === 'shopping'
              ? 'border-primary text-primary font-semibold'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <ShoppingCart size={18} />
          Liste de courses
        </button>
        <button
          onClick={() => setSelectedTab('activity')}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 transition ${
            selectedTab === 'activity'
              ? 'border-primary text-primary font-semibold'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <Activity size={18} />
          Activité
        </button>
      </div>

      {/* Overview Tab */}
      {selectedTab === 'overview' && (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            <StatCard
              title="Total produits"
              value={stats.overview.totalProducts}
              icon={Package}
              color="text-primary"
            />
            <StatCard
              title="Stock bas"
              value={stats.overview.lowStockCount}
              icon={AlertTriangle}
              color="text-orange-600"
            />
            <StatCard
              title="Rupture de stock"
              value={stats.overview.outOfStockCount}
              icon={AlertTriangle}
              color="text-red-600"
            />
            <StatCard
              title="Fournisseurs"
              value={stats.overview.totalSuppliers}
              icon={Users}
              color="text-green-600"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <StatCard
              title="Valeur totale du stock"
              value={`${stats.overview.totalValue}€`}
              icon={DollarSign}
              color="text-purple-600"
              subtitle="Basé sur les prix actuels"
            />
            <StatCard
              title="Prix moyen"
              value={`${stats.overview.avgPrice}€`}
              icon={BarChart3}
              color="text-indigo-600"
              subtitle="Moyenne de tous les produits"
            />
          </div>

          {/* Low Stock Products */}
          {stats.lowStockProducts && stats.lowStockProducts.length > 0 && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="text-orange-600" size={24} />
                  Produits en stock bas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Produit</th>
                        <th className="text-left p-2">Catégorie</th>
                        <th className="text-left p-2">Stock actuel</th>
                        <th className="text-left p-2">Stock idéal</th>
                        <th className="text-left p-2">Fournisseur</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.lowStockProducts.map((product) => (
                        <tr key={product.id} className="border-b hover:bg-gray-50">
                          <td className="p-2 font-medium">{product.name}</td>
                          <td className="p-2">{product.category}</td>
                          <td className="p-2">
                            <span className={`px-2 py-1 rounded text-sm ${
                              product.currentStock === 0
                                ? 'bg-red-100 text-red-800'
                                : 'bg-orange-100 text-orange-800'
                            }`}>
                              {product.currentStock}
                            </span>
                          </td>
                          <td className="p-2">{product.idealStock}</td>
                          <td className="p-2">{product.supplier}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Products by Category */}
          {stats.productsByCategory && stats.productsByCategory.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Produits par catégorie</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats.productsByCategory.map((cat, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="font-medium">{cat.name}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-48 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full"
                            style={{
                              width: `${(cat.count / stats.overview.totalProducts) * 100}%`
                            }}
                          />
                        </div>
                        <span className="text-sm text-gray-600 w-12 text-right">
                          {cat.count}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Price Trends Tab */}
      {selectedTab === 'trends' && (
        <div className="grid grid-cols-1 gap-4">
          {priceTrends.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-gray-500">
                Aucune donnée de prix disponible pour les 90 derniers jours
              </CardContent>
            </Card>
          ) : (
            priceTrends.map((trend) => (
              <Card key={trend.productId}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{trend.productName}</span>
                    <div className="flex items-center gap-2">
                      {trend.trend === 'up' && (
                        <span className="flex items-center gap-1 text-red-600 text-sm">
                          <TrendingUp size={16} />
                          +{trend.changePercent}%
                        </span>
                      )}
                      {trend.trend === 'down' && (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <TrendingDown size={16} />
                          {trend.changePercent}%
                        </span>
                      )}
                      {trend.trend === 'stable' && (
                        <span className="text-gray-600 text-sm">Stable</span>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600">Prix actuel</p>
                      <p className="text-lg font-bold">{trend.currentPrice ? parseFloat(trend.currentPrice).toFixed(2) : 'N/A'}€</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Prix min</p>
                      <p className="text-lg font-bold text-green-600">{parseFloat(trend.minPrice).toFixed(2)}€</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Prix max</p>
                      <p className="text-lg font-bold text-red-600">{parseFloat(trend.maxPrice).toFixed(2)}€</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Prix moyen</p>
                      <p className="text-lg font-bold text-primary">{trend.avgPrice}€</p>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    {trend.pricePoints.length} enregistrement(s) sur 90 jours
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Suppliers Tab */}
      {selectedTab === 'suppliers' && (
        <Card>
          <CardHeader>
            <CardTitle>Comparaison des fournisseurs</CardTitle>
          </CardHeader>
          <CardContent>
            {suppliers.length === 0 ? (
              <p className="text-center text-gray-500">Aucun fournisseur trouvé</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Fournisseur</th>
                      <th className="text-left p-2">Produits</th>
                      <th className="text-left p-2">Prix moyen</th>
                      <th className="text-left p-2">Total achats</th>
                      <th className="text-left p-2">Dernier achat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suppliers.map((supplier) => (
                      <tr key={supplier.supplierId} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">{supplier.supplierName}</td>
                        <td className="p-2">{supplier.productsCount}</td>
                        <td className="p-2">{supplier.avgPrice}€</td>
                        <td className="p-2">{supplier.totalPurchases}</td>
                        <td className="p-2">
                          {supplier.lastPurchaseDate
                            ? new Date(supplier.lastPurchaseDate).toLocaleDateString('fr-FR')
                            : 'Jamais'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Shopping Suggestions Tab */}
      {selectedTab === 'shopping' && (
        <div className="space-y-4">
          {/* Summary Card */}
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-700">Articles à commander</p>
                  <p className="text-2xl font-bold text-primary-darker">{suggestions.summary?.totalItems || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-700">Priorité haute</p>
                  <p className="text-2xl font-bold text-red-600">{suggestions.summary?.highPriority || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-700">Priorité moyenne</p>
                  <p className="text-2xl font-bold text-orange-600">{suggestions.summary?.mediumPriority || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-700">Coût estimé</p>
                  <p className="text-2xl font-bold text-green-600">{suggestions.summary?.totalEstimatedCost || 0}€</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Suggestions List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart size={24} />
                Suggestions de commande
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!suggestions.suggestions || suggestions.suggestions.length === 0 ? (
                <p className="text-center text-gray-500">Aucune suggestion de commande</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Priorité</th>
                        <th className="text-left p-2">Produit</th>
                        <th className="text-left p-2">Catégorie</th>
                        <th className="text-left p-2">Stock</th>
                        <th className="text-left p-2">À commander</th>
                        <th className="text-left p-2">Prix unitaire</th>
                        <th className="text-left p-2">Coût estimé</th>
                        <th className="text-left p-2">Fournisseur</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suggestions.suggestions.map((item) => (
                        <tr key={item.productId} className="border-b hover:bg-gray-50">
                          <td className="p-2">
                            <span
                              className={`px-2 py-1 rounded text-xs font-semibold ${
                                item.priority === 'high'
                                  ? 'bg-red-100 text-red-800'
                                  : item.priority === 'medium'
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {item.priority === 'high'
                                ? 'Haute'
                                : item.priority === 'medium'
                                ? 'Moyenne'
                                : 'Basse'}
                            </span>
                          </td>
                          <td className="p-2 font-medium">{item.productName}</td>
                          <td className="p-2">{item.category}</td>
                          <td className="p-2">{item.currentStock}/{item.idealStock}</td>
                          <td className="p-2 font-bold">{item.quantityNeeded}</td>
                          <td className="p-2">{parseFloat(item.lastPrice).toFixed(2)}€</td>
                          <td className="p-2 font-bold text-green-600">{item.estimatedCost}€</td>
                          <td className="p-2">{item.supplier?.name || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Activity Tab */}
      {selectedTab === 'activity' && (
        <ActivityTab stats={movementStats} />
      )}
    </div>
  );
};

const PIE_COLORS = ['#0d9488', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const ActivityTab = ({ stats }) => {
  if (!stats) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-500">
          Aucune donnee d'activite disponible
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* KPI cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-gray-600">Mouvements (7j)</p>
            <p className="text-2xl font-bold text-primary">{stats.totalMovements}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-gray-600">Entrees</p>
            <p className="text-2xl font-bold text-green-600">{stats.totalEntries}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-gray-600">Sorties</p>
            <p className="text-2xl font-bold text-red-600">{stats.totalExits}</p>
          </CardContent>
        </Card>
      </div>

      {/* Bar chart - 7 days */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity size={20} />
            Mouvements des 7 derniers jours
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.daily && stats.daily.some(d => d.total > 0) ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stats.daily} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value, name) => [value, name === 'entries' ? 'Entrees' : 'Sorties']}
                  labelFormatter={(label) => label}
                />
                <Legend formatter={(value) => value === 'entries' ? 'Entrees' : 'Sorties'} />
                <Bar dataKey="entries" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="exits" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-gray-400 py-8">Aucun mouvement sur les 7 derniers jours</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Pie chart - by category */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Par categorie</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.byCategory && stats.byCategory.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={stats.byCategory}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {stats.byCategory.map((entry, index) => (
                      <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [value, 'Mouvements']} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-gray-400 py-8">Aucune donnee</p>
            )}
          </CardContent>
        </Card>

        {/* Top 5 products */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top 5 produits les plus actifs</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.topProducts && stats.topProducts.length > 0 ? (
              <div className="space-y-3">
                {stats.topProducts.map((p, i) => (
                  <div key={p.name} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">{p.name}</div>
                      <div className="flex gap-3 text-xs text-gray-500">
                        <span className="text-green-600">+{p.entries}</span>
                        <span className="text-red-600">-{p.exits}</span>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-gray-700">{p.total} mvts</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-400 py-8">Aucune donnee</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* By person */}
      {stats.byPerson && stats.byPerson.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Activite par personne</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {stats.byPerson.map((p) => (
                <div key={p.name} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                    {p.initials}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{p.name}</div>
                    <div className="text-xs text-gray-500">{p.count} mouvements</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
